import React, { useMemo, ReactElement } from 'react';
import Loader from '../../../../components/loader/loader.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import ControlButton from '../../../../components/control-button/control-button.component';
import ControlButtonContainer from '../../../../components/control-button-container/control-button-container.component';
import FixedBottomContainer from '../../../../components/fixed-bottom-container/fixed-bottom-container.component';
import ExportIcon from '../../../../icons/export-icon';
import SaveIcon from '../../../../icons/save-icon';
import CancelFilledIcon from '../../../../icons/cancel-filled-icon';
import useScreenSmall from '../../../../hooks/use-screen-small';

const ControlButtonBlock: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const LABEL_CANCEL_DEFAULT: string = 'Cancel';
  const LABEL_SAVE_DEFAULT: string = 'Save';
  const LABEL_EXPORT_DEFAULT: string = 'Export';

  const {
    isModal,
    isExportMode,
    isEditMode,
    isSelectMode,
    isCustomizeMode,
    isModalEditMode,
    isNewEntity,
    isIconView,
    isSaveDefault,
    isSaveDisabled,
    isExportDisabled,
    withLoader,
    withOffset,
    withNoCancel,
    withSaveButton,
    labelCancel,
    labelSave,
    savePermission,
    onCancelForm,
    onSubmitForm,
    onExportClick,
    extraButtonComponent,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const isCentered: boolean = isSmallScreen || isModal;
  const isFixed: boolean = !isIconView && (isEditMode || (isNewEntity && !isModal) || isCustomizeMode
    || isExportMode || isSelectMode);
  const isInvisible: boolean = !isEditMode && !isNewEntity && !isModalEditMode
    && !isCustomizeMode && !isExportMode && !isSelectMode && !withSaveButton && !extraButtonComponent;
  const withTopOffset: boolean = !isIconView && !isFixed;
  const withLargeTopOffset: boolean = isModal;
  const loaderTopOffset: number = -10;
  const loaderLeftOffset: number | undefined = isFixed ? -50 : undefined;

  const savePermissions: string[] = useMemo((): string[] => (
    savePermission ? [savePermission] : []
  ), [savePermission]);

  return (
    <FixedBottomContainer isActive={isFixed} withOffset={withOffset}>
      <ControlButtonContainer
        isCentered={isCentered}
        isInvisible={isInvisible}
        withTopOffset={withTopOffset}
        withLargeTopOffset={withLargeTopOffset}
      >
        {
          withLoader && !isIconView && (
            <Loader isInline isFloating top={loaderTopOffset} left={loaderLeftOffset} />
          )
        }
        {
          (isEditMode || isModalEditMode || isNewEntity || isCustomizeMode || isExportMode || isSelectMode)
          && !withNoCancel && (
            <ControlButton
              isModal={isModal}
              isIconButton={isIconView}
              onClick={onCancelForm}
              icon={isIconView ? <CancelFilledIcon /> : undefined}
            >
              {labelCancel || LABEL_CANCEL_DEFAULT}
            </ControlButton>
          )
        }
        {extraButtonComponent}
        {
          (isEditMode || isModalEditMode || isNewEntity || withSaveButton) && (
            <CheckAccess isEnabled={savePermissions && !!savePermissions.length} permissions={savePermissions}>
              <ControlButton
                isActive={!isSaveDefault}
                isModal={isModal}
                isIconButton={isIconView}
                isDisabled={isSaveDisabled}
                icon={isIconView ? <SaveIcon /> : undefined}
                onClick={onSubmitForm}
              >
                {labelSave || LABEL_SAVE_DEFAULT}
              </ControlButton>
            </CheckAccess>
          )
        }
        {
          isExportMode && (
            <ControlButton
              isActive
              isModal={isModal}
              isIconButton={isIconView}
              isDisabled={isExportDisabled}
              icon={isIconView ? <ExportIcon /> : undefined}
              onClick={onExportClick}
            >
              {LABEL_EXPORT_DEFAULT}
            </ControlButton>
          )
        }
      </ControlButtonContainer>
    </FixedBottomContainer>
  );
});

export default ControlButtonBlock;
